/**
 * TermsDialog — 服務條款 / 隱私條款彈窗（受控元件）
 *
 * 設計：
 *   - 受控（controlled）：open / onOpenChange 由「消費端」持有並傳入，
 *     本元件不自持開關狀態（多專案共用時最單純、可預期）。
 *   - 兩個 tab：隱私條款 / 服務條款，可用 defaultTab 指定初始頁籤。
 *   - 站台名以 siteName prop 傳入，內插到文案的 {siteName}。
 *   - 語系：預設讀 ApiContext 的 lng（可用 lng prop 覆寫），
 *     以 convertLanguageCode 轉成 zh_TW / en_US...，查無則 fallback zh_TW。
 *   - SDK 無 UI 庫，樣式一律 inline；可用 overlayStyle / contentStyle 覆寫。
 *
 * 用法（消費端自行持有 state）：
 *   const [open, setOpen] = useState(false);
 *   <TermsDialog open={open} onOpenChange={setOpen} siteName="星曜娛樂城" />
 */

import { useEffect, useState } from 'react';

import { getTermsContent, interpolate } from './termsContent';

const TABS = ['terms', 'privacy'];

/** 暗色調配色（可用 overlayStyle / contentStyle 覆寫） */
const C = {
  overlay: 'rgba(0, 0, 0, 0.72)',
  cardBg: '#1b1b22',
  border: '#2c2c36',
  title: '#f2f2f5',
  heading: '#ededf2',
  body: '#c4c4cc',
  muted: '#8a8a94',
  accent: '#8b9cff',
  tabActive: '#ffffff',
  tabInactive: '#8a8a94'
};

const TermsDialog = ({
  open = false,
  onOpenChange,
  siteName = '',
  siteUrl = '',
  defaultTab = 'privacy',
  lng: lngProp,
  zIndex = 99999,
  overlayStyle = {},
  contentStyle = {}
}) => {
  // 注意：magic 未串接 cli-integrated-api，無 ApiContext / convertLanguageCode。
  // 文案目前僅提供 zh_TW；getTermsContent 對未知語系一律 fallback 到 zh_TW。
  const content = getTermsContent(lngProp);

  // 內插用變數：{siteName} / {siteUrl}
  const vars = { siteName, siteUrl };

  const initialTab = TABS.includes(defaultTab) ? defaultTab : 'privacy';
  const [activeTab, setActiveTab] = useState(initialTab);

  // 每次由「關 → 開」時，回到預設頁籤。
  // 用 render 期間比對前次 open 來調整 state（React 官方建議做法），
  // 避免在 effect 內同步 setState 造成串連 re-render。
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setActiveTab(initialTab);
  }

  // 開啟時：ESC 關閉 + 鎖住底層捲動
  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') onOpenChange?.(false);
    };
    document.addEventListener('keydown', onKeyDown);

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onOpenChange]);

  if (!open) return null;

  const section = content[activeTab];

  const close = () => onOpenChange?.(false);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={section?.title}
      onClick={close}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: C.overlay,
        backdropFilter: 'blur(2px)',
        padding: 16,
        boxSizing: 'border-box',
        ...overlayStyle
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 560,
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          background: C.cardBg,
          color: C.body,
          borderRadius: 12,
          border: `1px solid ${C.border}`,
          overflow: 'hidden',
          boxShadow: '0 16px 48px rgba(0, 0, 0, 0.6)',
          ...contentStyle
        }}
      >
        {/* Header：tabs + 關閉 */}
        <div
          style={{
            display: 'flex',
            alignItems: 'stretch',
            borderBottom: `1px solid ${C.border}`,
            flexShrink: 0
          }}
        >
          {TABS.map((tab) => {
            const activeStyle =
              activeTab === tab
                ? {
                    color: C.tabActive,
                    fontWeight: 700,
                    borderBottom: `2px solid ${C.accent}`
                  }
                : {
                    color: C.tabInactive,
                    fontWeight: 500,
                    borderBottom: '2px solid transparent'
                  };
            return (
              <button
                key={tab}
                type="button"
                onClick={() => setActiveTab(tab)}
                style={{
                  flex: 1,
                  padding: '14px 8px',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: 15,
                  ...activeStyle
                }}
              >
                {content.tabs[tab]}
              </button>
            );
          })}
          <button
            type="button"
            aria-label={content.close}
            onClick={close}
            style={{
              padding: '0 16px',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontSize: 20,
              color: C.muted
            }}
          >
            ✕
          </button>
        </div>

        {/* Body：可捲動內文 */}
        <div style={{ padding: 20, overflowY: 'auto' }}>
          <h2 style={{ margin: '0 0 4px', fontSize: 20, color: C.title }}>
            {interpolate(section?.title, vars)}
          </h2>
          {section?.updatedAt && (
            <p style={{ margin: '0 0 16px', fontSize: 12, color: C.muted }}>
              {content.updatedLabel}：{section.updatedAt}
            </p>
          )}

          {section?.sections?.map((sec, i) => (
            <div key={i} style={{ marginBottom: 16 }}>
              {sec.heading ? (
                <h3
                  style={{
                    margin: '0 0 6px',
                    fontSize: 15,
                    fontWeight: 700,
                    color: C.heading
                  }}
                >
                  {interpolate(sec.heading, vars)}
                </h3>
              ) : null}
              {sec.body.map((p, j) => (
                <p
                  key={j}
                  style={{
                    margin: '0 0 8px',
                    fontSize: 13,
                    lineHeight: 1.8,
                    color: C.body
                  }}
                >
                  {interpolate(p, vars)}
                </p>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TermsDialog;
export { TermsDialog };
